<template>
  <v-container fluid class="py-0 px-2">
    <v-text-field
      v-model="search"
      background-color="colorSearchSettings"
      clearable
      :append-icon="mdiMagnify"
      :label="$t('label.search')"
      hide-details
    ></v-text-field>
    <div style="min-height: 1px"></div>
    <v-data-table
      :headers="headers"
      :items="tableData"
      item-key="id"
      :height="$store.state.windowSize.y - 150"
      :search="search"
      fixed-header
      must-sort
      disable-pagination
      hide-default-footer
      class="tableCursor"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.name }}</td>
          <td>
            <v-icon :color="item.planners.length > 0 ? 'green' : 'red'">
              {{ item.planners.length > 0 ? mdiCheckBold : mdiCloseThick }}
            </v-icon>
            {{ item.planners.length }}
          </td>
          <td>
            <v-icon :color="item.authorisers.length > 0 ? 'green' : 'red'">
              {{ item.authorisers.length > 0 ? mdiCheckBold : mdiCloseThick }}
            </v-icon>
            {{ item.authorisers.length }}
          </td>
          <td style="max-width: 300px; overflow: hidden; white-space: nowrap">{{ item.note }}</td>
          <td
            v-if="$store.state.auth.userSettings.accEmployeeGroups.update"
            class="px-2 tableSelectCursor"
            @click="((dialogEdit = true), (employeeGroup = { ...item }))"
          >
            <v-icon>{{ mdiPen }}</v-icon>
          </td>
          <td v-else></td>
        </tr>
      </template>
    </v-data-table>
    <v-row>
      <v-col cols="3">
        <v-btn v-if="$store.state.auth.userSettings.accEmployeeGroups.write" block color="colorNew" @click="dataNew">
          {{ $t('label.addNew') }}
        </v-btn>
      </v-col>
      <v-col cols="3">
        <v-btn v-if="$store.state.auth.userSettings.accEmployeeGroups.write" block color="colorEdit" @click="globalGroupEdit">
          {{ $t('btn.globalGroup') }}
        </v-btn>
      </v-col>
    </v-row>
    <!----------DIALOGS------------------------------>
    <v-dialog v-model="dialogEdit" persistent width="70%">
      <v-card>
        <v-card-title class="headline colorHeadlineStandard" primary-title>
          {{ $tc('label.planningGroup') }}
          <v-spacer />
          <v-icon large @click="dialogEdit = false">{{ mdiClose }}</v-icon>
        </v-card-title>
        <v-card-text class="pa-3">
          <v-form v-model="validForm" ref="employeeGroupForm">
            <v-row>
              <v-col>
                <v-text-field
                  :label="$t('label.itemName')"
                  v-model="employeeGroup.name"
                  :rules="[ruleRequired]"
                  maxlength="256"
                  required
                  hide-details
                  :disabled="employeeGroup.id == 0"
                  dense
                  autofocus
                />
              </v-col>
            </v-row>
            <v-row v-if="employeeGroup.id != 0">
              <v-col>
                <v-autocomplete
                  v-model="employeeGroup.areas"
                  :label="$tc('label.area', 2)"
                  :items="listAreas"
                  item-text="name"
                  item-value="id"
                  :menu-props="{ minWidth: '500' }"
                  :return-object="false"
                  multiple
                  chips
                  deletable-chips
                  append-icon="$dropdown"
                  hide-details
                  dense
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-autocomplete
                  v-model="employeeGroup.planners"
                  :label="$t('label.visitPlanner')"
                  :items="listEmployees"
                  item-text="comboBoxName"
                  item-value="id"
                  :menu-props="{ minWidth: '500' }"
                  :return-object="false"
                  multiple
                  chips
                  deletable-chips
                  append-icon="$dropdown"
                  hide-details
                  dense
                />
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  v-model="employeeGroup.authorisers"
                  :label="$t('label.visitApprover')"
                  :items="listEmployees"
                  item-text="comboBoxName"
                  item-value="id"
                  :menu-props="{ minWidth: '500' }"
                  :return-object="false"
                  multiple
                  chips
                  deletable-chips
                  append-icon="$dropdown"
                  hide-details
                  dense
                />
              </v-col>
            </v-row>
            <v-row v-if="employeeGroup.id != 0">
              <v-col cols="6" />
              <v-col class="py-0" cols="6">
                <v-checkbox class="my-0" :label="$tc('label.notifyAuthorisersByDefault')" v-model="employeeGroup.notify" hide-details />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-textarea
                  :label="$t('label.note')"
                  v-model="employeeGroup.note"
                  maxlength="512"
                  name="input-7-1"
                  no-resize
                  hide-details
                  dense
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn
            v-if="employeeGroup.id > 0 && $store.state.auth.userSettings.accEmployeeGroups.delete"
            color="colorDelete"
            @click="modalDelete = true"
          >
            {{ $t('btn.delete') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="colorSave" @click="dataSave">{{ $t('btn.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogConfirmDelete
      :header="$t('label.delete')"
      :body="`${$t('msg.confirmDelete')}: ${employeeGroup.name}?`"
      :openDialog.sync="modalDelete"
      v-on:confirm="dataDelete"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import DialogConfirmDelete from '@/components/DialogConfirmDelete.vue';

import { mdiMagnify, mdiPen, mdiClose, mdiCloseThick, mdiCheckBold } from '@mdi/js';

export default Vue.extend({
  name: 'SettingsEmployeeGroups',
  components: { DialogConfirmDelete },
  data: () => ({
    tableData: [],
    dialogEdit: false,
    modalDelete: false,
    validForm: false,
    search: '',
    listEmployees: [],
    listAreas: [],
    globalGroup: { id: -1, name: '', note: '', planners: [], authorisers: [], notify: false, areas: [] },
    employeeGroup: {
      id: -1,
      name: '',
      note: '',
      planners: [],
      authorisers: [],
      notify: false,
      areas: [],
    },
    mdiMagnify: mdiMagnify,
    mdiPen: mdiPen,
    mdiClose: mdiClose,
    mdiCloseThick: mdiCloseThick,
    mdiCheckBold: mdiCheckBold,
  }),
  computed: {
    ruleRequired() {
      return (v: any) => !!v || this.$i18n.t('msg.fieldIsMandatory');
    },
    headers: function () {
      return [
        { text: this.$i18n.t('label.itemName'), value: 'name', width: '20%' },
        { text: this.$i18n.t('label.visitPlanning'), value: 'planners', width: '15%' },
        { text: this.$i18n.tc('label.approval'), value: 'authorisers', width: '15%' },
        { text: this.$i18n.t('label.note'), value: 'note' },
        { sortable: false, width: '48px' },
      ];
    },
  },
  mounted() {
    this.listEmployeesLoad();
    this.listAreasLoad();
    this.dataLoad();
  },
  methods: {
    dataLoad() {
      axios
        .get('/api/v1/employeegroup')
        .then((response) => {
          const global = response.data // Zmaz globalni skupinu pro planovani
            .map((item: any) => {
              return item.id;
            })
            .indexOf(0);
          this.globalGroup = response.data[global];
          response.data.splice(global, 1);
          this.tableData = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    globalGroupEdit() {
      this.employeeGroup = { ...this.globalGroup };
      this.dialogEdit = true;
    },
    dataNew() {
      const defaults: any = this.$options;
      this.employeeGroup = { ...defaults.data().employeeGroup };
      this.dialogEdit = true;
      this.$nextTick(function () {
        const form: any = this.$refs.employeeGroupForm;
        form.validate();
      });
    },
    dataSave() {
      const form: any = this.$refs.employeeGroupForm;
      if (form.validate() !== true) {
        this.$store.commit('globalMessage', this.$i18n.t('msg.formNotValid'));
      } else {
        if (this.employeeGroup.id === -1) {
          axios
            .post('/api/v1/employeegroup', this.employeeGroup)
            .then((response) => {
              if (response.data.status === 'recordExists') {
                this.$store.commit('globalMessage', this.$i18n.t('msg.itemNameExists'));
              } else {
                this.dataLoad();
                this.dialogEdit = false;
              }
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        } else {
          axios
            .put('/api/v1/employeegroup', this.employeeGroup)
            .then(() => {
              this.dataLoad();
              this.dialogEdit = false;
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        }
      }
    },
    dataDelete() {
      axios
        .delete(`/api/v1/employeegroup/${this.employeeGroup.id}`)
        .then(() => {
          this.dataLoad();
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
      this.dialogEdit = false;
      this.modalDelete = false;
    },
    listAreasLoad() {
      axios
        .get('/api/v1/area?planning=true')
        .then((response) => {
          this.listAreas = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    listEmployeesLoad() {
      axios
        .get(`/api/v1/employee`)
        .then((response) => {
          for (const employee of response.data) {
            employee.comboBoxName = employee.surname + ' ' + employee.name;
            if (employee.department != '') {
              employee.comboBoxName = employee.comboBoxName + ' (' + employee.department + ')';
            }
          }
          this.listEmployees = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
  },
});
</script>
